import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Switch,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import { mfpModelsAPI } from '../services/api';
import CostBreakdownCalculator from '../components/CostBreakdownCalculator';

interface MFPModel {
  id: string;
  manufacturer: string;
  model_name: string;
  model_code: string;
  equipment_cost: number;
  base_equipment_cost?: number;
  distributor_margin_pct?: number;
  import_tax_pct?: number;
  total_equipment_cost?: number;
  is_color: boolean;
  is_active: boolean;
  is_sellable: boolean;
  image_url?: string;
}

export default function SalesCatalog() {
  const [models, setModels] = useState<MFPModel[]>([]);
  const [filteredModels, setFilteredModels] = useState<MFPModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlySellable, setShowOnlySellable] = useState(false);
  const [updatingModel, setUpdatingModel] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedModelForUpload, setSelectedModelForUpload] = useState<string | null>(null);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [selectedModelForCost, setSelectedModelForCost] = useState<MFPModel | null>(null);
  const [costBreakdown, setCostBreakdown] = useState({
    baseCost: 0,
    distributorMarginPct: 3.00,
    importTaxPct: 10.00,
    totalCost: 0
  });
  const [savingCost, setSavingCost] = useState(false);

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    filterModels();
  }, [models, searchTerm, showOnlySellable]);

  const loadModels = async () => {
    try {
      setLoading(true);
      const data = await mfpModelsAPI.getAll({ is_active: true });
      setModels(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load models:', err);
      setError(err.response?.data?.detail || 'Failed to load MFP models');
    } finally {
      setLoading(false);
    }
  };

  const filterModels = () => {
    let filtered = models;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.manufacturer.toLowerCase().includes(term) ||
          m.model_name.toLowerCase().includes(term) ||
          m.model_code.toLowerCase().includes(term)
      );
    }

    // Filter by sellable status
    if (showOnlySellable) {
      filtered = filtered.filter((m) => m.is_sellable);
    }

    setFilteredModels(filtered);
  };

  const handleToggleSellable = async (modelId: string, currentValue: boolean) => {
    try {
      setUpdatingModel(modelId);
      await mfpModelsAPI.toggleSellable(modelId, !currentValue);

      // Update local state
      setModels((prev) =>
        prev.map((m) =>
          m.id === modelId ? { ...m, is_sellable: !currentValue } : m
        )
      );
    } catch (err: any) {
      console.error('Failed to toggle sellable status:', err);
      alert(err.response?.data?.detail || 'Failed to update model status');
    } finally {
      setUpdatingModel(null);
    }
  };

  const handleImageUploadClick = (modelId: string) => {
    setSelectedModelForUpload(modelId);
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedModelForUpload) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(selectedModelForUpload);
      const response = await mfpModelsAPI.uploadImage(selectedModelForUpload, file);

      // Update local state with new image URL
      setModels((prev) =>
        prev.map((m) =>
          m.id === selectedModelForUpload ? { ...m, image_url: response.image_url } : m
        )
      );

      alert('Image uploaded successfully!');
    } catch (err: any) {
      console.error('Failed to upload image:', err);
      alert(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
      setSelectedModelForUpload(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageDelete = async (modelId: string) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setUploadingImage(modelId);
      await mfpModelsAPI.deleteImage(modelId);

      // Update local state to remove image URL
      setModels((prev) =>
        prev.map((m) =>
          m.id === modelId ? { ...m, image_url: undefined } : m
        )
      );

      alert('Image deleted successfully!');
    } catch (err: any) {
      console.error('Failed to delete image:', err);
      alert(err.response?.data?.detail || 'Failed to delete image');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleCostEditClick = (model: MFPModel) => {
    setSelectedModelForCost(model);
    setCostBreakdown({
      baseCost: model.base_equipment_cost || model.equipment_cost || 0,
      distributorMarginPct: model.distributor_margin_pct || 3.00,
      importTaxPct: model.import_tax_pct || 10.00,
      totalCost: model.total_equipment_cost || model.equipment_cost || 0
    });
    setCostDialogOpen(true);
  };

  const handleCostDialogClose = () => {
    setCostDialogOpen(false);
    setSelectedModelForCost(null);
  };

  const handleCostSave = async () => {
    if (!selectedModelForCost) return;

    try {
      setSavingCost(true);
      await mfpModelsAPI.updateCostBreakdown(selectedModelForCost.id, {
        base_equipment_cost: costBreakdown.baseCost,
        distributor_margin_pct: costBreakdown.distributorMarginPct,
        import_tax_pct: costBreakdown.importTaxPct
      });

      // Update local state
      setModels((prev) =>
        prev.map((m) =>
          m.id === selectedModelForCost.id
            ? {
                ...m,
                base_equipment_cost: costBreakdown.baseCost,
                distributor_margin_pct: costBreakdown.distributorMarginPct,
                import_tax_pct: costBreakdown.importTaxPct,
                total_equipment_cost: costBreakdown.totalCost,
                equipment_cost: costBreakdown.totalCost
              }
            : m
        )
      );

      handleCostDialogClose();
      alert('Cost breakdown updated successfully!');
    } catch (err: any) {
      console.error('Failed to update cost breakdown:', err);
      alert(err.response?.data?.detail || 'Failed to update cost breakdown');
    } finally {
      setSavingCost(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const sellableCount = models.filter((m) => m.is_sellable).length;

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Sales Catalog Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control which MFP models are visible to Ventas users for new sales proposals
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            placeholder="Search by manufacturer, model name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showOnlySellable}
                onChange={(e) => setShowOnlySellable(e.target.checked)}
              />
            }
            label="Show only sellable"
          />
          <Chip
            label={`${sellableCount} Sellable Models`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleImageUpload}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Image</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Model Name</TableCell>
              <TableCell>Model Code</TableCell>
              <TableCell align="right">Equipment Cost</TableCell>
              <TableCell align="center">Type</TableCell>
              <TableCell align="center">Sellable for Ventas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredModels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    {searchTerm || showOnlySellable
                      ? 'No models match your filters'
                      : 'No models found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredModels.map((model) => (
                <TableRow
                  key={model.id}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                    opacity: updatingModel === model.id || uploadingImage === model.id ? 0.6 : 1,
                  }}
                >
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      {model.image_url ? (
                        <>
                          <Avatar
                            src={model.image_url}
                            alt={model.model_name}
                            variant="rounded"
                            sx={{ width: 60, height: 60 }}
                          />
                          <Tooltip title="Delete image">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleImageDelete(model.id)}
                              disabled={uploadingImage === model.id}
                            >
                              {uploadingImage === model.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Upload image">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleImageUploadClick(model.id)}
                            disabled={uploadingImage === model.id}
                          >
                            {uploadingImage === model.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <UploadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{model.manufacturer}</TableCell>
                  <TableCell>{model.model_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {model.model_code}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                      <Typography variant="body2">
                        ${(model.total_equipment_cost || model.equipment_cost).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      <Tooltip title="Edit cost breakdown">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleCostEditClick(model)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={model.is_color ? 'Color' : 'B&W'}
                      size="small"
                      color={model.is_color ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Switch
                        checked={model.is_sellable}
                        onChange={() => handleToggleSellable(model.id, model.is_sellable)}
                        disabled={updatingModel === model.id}
                        color="success"
                      />
                      {model.is_sellable ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="disabled" fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          Showing {filteredModels.length} of {models.length} models
        </Typography>
      </Box>

      {/* Cost Breakdown Dialog */}
      <Dialog open={costDialogOpen} onClose={handleCostDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Cost Breakdown
          {selectedModelForCost && (
            <Typography variant="body2" color="text.secondary">
              {selectedModelForCost.manufacturer} {selectedModelForCost.model_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <CostBreakdownCalculator
              baseCost={costBreakdown.baseCost}
              distributorMarginPct={costBreakdown.distributorMarginPct}
              importTaxPct={costBreakdown.importTaxPct}
              onChange={setCostBreakdown}
              label="Equipment Cost Breakdown"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCostDialogClose} disabled={savingCost}>
            Cancel
          </Button>
          <Button
            onClick={handleCostSave}
            variant="contained"
            disabled={savingCost}
            startIcon={savingCost ? <CircularProgress size={20} /> : null}
          >
            {savingCost ? 'Saving...' : 'Save Cost Breakdown'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
